import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  X, Copy, Check, Share2, QrCode, Wifi, WifiOff,
  Smartphone, ChevronRight, ExternalLink, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StreamShareModal({ 
  rtmpUrl, 
  streamKey, 
  srtUrl,
  srtStreamId,
  courtName,
  isLive,
  onClose 
}) {
  const [copiedField, setCopiedField] = useState(null);
  const [activeTab, setActiveTab] = useState('qr');
  const [protocol, setProtocol] = useState('rtmps');

  const fullRtmpUrl = `${rtmpUrl?.replace(/\/$/, '')}/${streamKey}`;
  const fullSrtUrl = srtUrl && srtStreamId ? `${srtUrl}?streamid=${srtStreamId}` : '';

  const copyField = (field, value) => {
    try {
      navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopiedField(field);
    toast.success('Copied!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const shareCredentials = async () => {
    const text = protocol === 'srt' && fullSrtUrl
      ? `Stream Setup for ${courtName || 'Court'}\n\nSRT URL: ${fullSrtUrl}\n\nXBotGo/OBS Setup:\n1. Select SRT protocol\n2. Paste the SRT URL above\n3. Start streaming`
      : `Stream Setup for ${courtName || 'Court'}\n\nRTMP URL: ${rtmpUrl}\nStream Key: ${streamKey}\nFull URL: ${fullRtmpUrl}\n\nXBotGo Setup:\n1. Open XBotGo app\n2. Tap Live → Select RTMP\n3. Paste the RTMP URL and Stream Key above\n4. Tap Start Live Streaming`;

    if (navigator.share) {
      try {
        await navigator.share({ title: `Stream Key - ${courtName}`, text });
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
    copyField('all', text);
    toast.success('Credentials copied to clipboard');
  };

  // QR data: encode stream credentials directly so any QR scanner can read them
  const qrData = protocol === 'srt' && fullSrtUrl
    ? `Stream: ${courtName || 'Court'}\nSRT URL: ${fullSrtUrl}`
    : `Stream: ${courtName || 'Court'}\nRTMP URL: ${rtmpUrl || ''}\nStream Key: ${streamKey || ''}\nFull URL: ${fullRtmpUrl}`;

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="stream-share-modal">
      <div className="bg-zinc-900 rounded-2xl border-2 border-zinc-700 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-700 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <div className="flex items-center gap-3">
            <QrCode className="w-5 h-5 text-cyan-400" />
            <div>
              <h2 className="text-lg font-bold text-white">Share Stream Key</h2>
              <p className="text-xs text-zinc-500">{courtName || 'Stream'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isLive ? (
              <Badge className="bg-emerald-600/80 text-white text-xs">
                <Wifi className="w-3 h-3 mr-1" /> Live
              </Badge>
            ) : (
              <Badge variant="outline" className="text-zinc-500 border-zinc-600 text-xs">
                <WifiOff className="w-3 h-3 mr-1" /> Offline
              </Badge>
            )}
            <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full" data-testid="close-share-modal">
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-zinc-800">
          <button
            onClick={() => setActiveTab('qr')}
            className={`flex-1 py-3 text-sm font-medium transition-all ${
              activeTab === 'qr' 
                ? 'text-cyan-400 border-b-2 border-cyan-400' 
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <QrCode className="w-4 h-4 inline mr-1.5" />
            QR Code
          </button>
          <button
            onClick={() => setActiveTab('manual')}
            className={`flex-1 py-3 text-sm font-medium transition-all ${
              activeTab === 'manual' 
                ? 'text-cyan-400 border-b-2 border-cyan-400' 
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <Copy className="w-4 h-4 inline mr-1.5" />
            Copy & Paste
          </button>
          <button
            onClick={() => setActiveTab('guide')}
            className={`flex-1 py-3 text-sm font-medium transition-all ${
              activeTab === 'guide' 
                ? 'text-cyan-400 border-b-2 border-cyan-400' 
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <Smartphone className="w-4 h-4 inline mr-1.5" />
            Setup Guide
          </button>
        </div>

        <div className="p-5">
          {/* QR Code Tab */}
          {activeTab === 'qr' && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400 text-center">
                Scan this QR code to open a page with the stream credentials ready to copy.
              </p>
              <div className="bg-white rounded-xl p-6 flex items-center justify-center mx-auto w-fit">
                <QRCodeSVG 
                  value={qrData} 
                  size={220} 
                  level="M"
                  includeMargin={false}
                />
              </div>
              <div className="text-center">
                <p className="text-xs text-zinc-600">
                  Opens a tap-to-copy page with RTMP URL + Stream Key
                </p>
              </div>
              <Button
                onClick={shareCredentials}
                className="w-full h-11 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold"
                data-testid="share-credentials-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share via Message / AirDrop
              </Button>
            </div>
          )}

          {/* Copy & Paste Tab */}
          {activeTab === 'manual' && (
            <div className="space-y-3">
              {/* Protocol Toggle */}
              {fullSrtUrl && (
                <div className="flex rounded-lg bg-zinc-800 p-1 mb-4" data-testid="protocol-toggle">
                  <button
                    onClick={() => setProtocol('rtmps')}
                    className={`flex-1 py-2 rounded-md text-xs font-semibold transition-all ${
                      protocol === 'rtmps' ? 'bg-emerald-600 text-white' : 'text-zinc-400'
                    }`}
                    data-testid="protocol-rtmps"
                  >
                    RTMPS
                  </button>
                  <button
                    onClick={() => setProtocol('srt')}
                    className={`flex-1 py-2 rounded-md text-xs font-semibold transition-all ${
                      protocol === 'srt' ? 'bg-blue-600 text-white' : 'text-zinc-400'
                    }`}
                    data-testid="protocol-srt"
                  >
                    SRT (WiFi-friendly)
                  </button>
                </div>
              )}

              {protocol === 'rtmps' ? (
                <>
                  <p className="text-sm text-zinc-400">
                    Copy each field and paste into your streaming app.
                  </p>

                  {/* RTMP URL */}
                  <div className="bg-zinc-800 rounded-lg p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500 font-medium">RTMP Server URL</span>
                      <button
                        onClick={() => copyField('rtmp', rtmpUrl)}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-zinc-700 hover:bg-zinc-600 transition-colors"
                        data-testid="copy-rtmp-manual"
                      >
                        {copiedField === 'rtmp' ? (
                          <><Check className="w-3 h-3 text-green-400" /><span className="text-xs text-green-400">Copied</span></>
                        ) : (
                          <><Copy className="w-3 h-3 text-zinc-400" /><span className="text-xs text-zinc-400">Copy</span></>
                        )}
                      </button>
                    </div>
                    <code className="text-sm text-emerald-400 font-mono break-all block">{rtmpUrl}</code>
                  </div>

                  {/* Stream Key */}
                  <div className="bg-zinc-800 rounded-lg p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500 font-medium">Stream Key</span>
                      <button
                        onClick={() => copyField('key', streamKey)}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-zinc-700 hover:bg-zinc-600 transition-colors"
                        data-testid="copy-key-manual"
                      >
                        {copiedField === 'key' ? (
                          <><Check className="w-3 h-3 text-green-400" /><span className="text-xs text-green-400">Copied</span></>
                        ) : (
                          <><Copy className="w-3 h-3 text-zinc-400" /><span className="text-xs text-zinc-400">Copy</span></>
                        )}
                      </button>
                    </div>
                    <code className="text-sm text-amber-400 font-mono break-all block">{streamKey}</code>
                  </div>

                  {/* Full URL */}
                  <div className="bg-zinc-800 rounded-lg p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500 font-medium">Full URL (for OBS / XBotGo)</span>
                      <button
                        onClick={() => copyField('full', fullRtmpUrl)}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-zinc-700 hover:bg-zinc-600 transition-colors"
                        data-testid="copy-full-manual"
                      >
                        {copiedField === 'full' ? (
                          <><Check className="w-3 h-3 text-green-400" /><span className="text-xs text-green-400">Copied</span></>
                        ) : (
                          <><Copy className="w-3 h-3 text-zinc-400" /><span className="text-xs text-zinc-400">Copy</span></>
                        )}
                      </button>
                    </div>
                    <code className="text-sm text-blue-400 font-mono break-all block">{fullRtmpUrl}</code>
                  </div>

                  {/* WiFi Warning */}
                  {fullSrtUrl && (
                    <div className="bg-amber-950/30 border border-amber-800/40 rounded-lg p-3 mt-2">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-amber-300 font-medium">Stream failing on WiFi?</p>
                          <p className="text-xs text-zinc-400 mt-1">
                            Some WiFi networks block RTMPS. Switch to <button onClick={() => setProtocol('srt')} className="text-blue-400 underline font-medium">SRT protocol</button> above, or use cellular data instead.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p className="text-sm text-zinc-400">
                    SRT is more reliable on restricted WiFi networks. Copy the full SRT URL below.
                  </p>

                  {/* SRT URL */}
                  <div className="bg-zinc-800 rounded-lg p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500 font-medium">SRT URL (single field)</span>
                      <button
                        onClick={() => copyField('srt', fullSrtUrl)}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-zinc-700 hover:bg-zinc-600 transition-colors"
                        data-testid="copy-srt-manual"
                      >
                        {copiedField === 'srt' ? (
                          <><Check className="w-3 h-3 text-green-400" /><span className="text-xs text-green-400">Copied</span></>
                        ) : (
                          <><Copy className="w-3 h-3 text-zinc-400" /><span className="text-xs text-zinc-400">Copy</span></>
                        )}
                      </button>
                    </div>
                    <code className="text-sm text-blue-400 font-mono break-all block">{fullSrtUrl}</code>
                  </div>

                  <div className="bg-blue-950/30 border border-blue-800/40 rounded-lg p-3">
                    <p className="text-xs text-blue-300 font-medium mb-1">SRT Setup (OBS)</p>
                    <ol className="text-xs text-zinc-400 space-y-1 list-decimal list-inside">
                      <li>Settings → Stream → Service: <strong className="text-white">Custom</strong></li>
                      <li>Server: paste the SRT URL above</li>
                      <li>Leave Stream Key empty</li>
                      <li>Start Streaming</li>
                    </ol>
                  </div>

                  <div className="bg-blue-950/30 border border-blue-800/40 rounded-lg p-3">
                    <p className="text-xs text-blue-300 font-medium mb-1">SRT Setup (XBotGo)</p>
                    <ol className="text-xs text-zinc-400 space-y-1 list-decimal list-inside">
                      <li>Live → Other Streaming → select <strong className="text-white">SRT</strong></li>
                      <li>Paste the SRT URL above</li>
                      <li>Start Live Streaming</li>
                    </ol>
                  </div>
                </>
              )}

              <Button
                onClick={shareCredentials}
                variant="outline"
                className="w-full h-10 border-zinc-600 text-zinc-300 hover:text-white"
                data-testid="share-all-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share All Credentials
              </Button>
            </div>
          )}

          {/* Setup Guide Tab */}
          {activeTab === 'guide' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Smartphone className="w-5 h-5 text-cyan-400" />
                <span className="text-sm font-semibold text-white">XBotGo Camera Setup</span>
              </div>

              <div className="space-y-3">
                <StepItem 
                  number={1} 
                  title="Open XBotGo App" 
                  description="Connect your XBotGo Chameleon camera and select the sport mode."
                />
                <StepItem 
                  number={2} 
                  title="Tap the Live Button" 
                  description="In the recording screen, tap the Live icon. Choose your quality (1080p recommended for good network)."
                />
                <StepItem 
                  number={3} 
                  title='Select "RTMP" Streaming' 
                  description='Choose "Other Streaming Options" then "RTMP" (not YouTube or Facebook).'
                />
                <StepItem 
                  number={4} 
                  title="Enter Stream Credentials" 
                  description="Paste the RTMP URL and Stream Key from the Copy & Paste tab."
                  action={
                    <Button
                      onClick={() => setActiveTab('manual')}
                      size="sm"
                      className="mt-2 bg-zinc-700 hover:bg-zinc-600 text-xs h-8"
                    >
                      Go to Copy & Paste <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  }
                />
                <StepItem 
                  number={5} 
                  title="Start Live Streaming" 
                  description="Tap 'Start Live Streaming'. The live icon turns red when connected. This console will show CONNECTED status."
                  highlight
                />
              </div>

              {/* WiFi Troubleshooting */}
              <div className="bg-amber-950/30 border border-amber-800/40 rounded-lg p-3 mt-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-amber-300 font-medium">WiFi Connection Issues?</p>
                    <ul className="text-xs text-zinc-400 mt-1 space-y-1">
                      <li>Try <strong className="text-white">cellular data</strong> instead of WiFi</li>
                      {fullSrtUrl && (
                        <li>Switch to <button onClick={() => { setActiveTab('manual'); setProtocol('srt'); }} className="text-blue-400 underline font-medium">SRT protocol</button> (more firewall-friendly)</li>
                      )}
                      <li>If on gym/school WiFi, ask IT to allow port 443 for RTMPS</li>
                      <li>Check your upload speed — need at least 3 Mbps for 720p</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-cyan-950/30 border border-cyan-800/40 rounded-lg p-3">
                <p className="text-xs text-cyan-300">
                  <strong>Tip:</strong> Use the QR code tab to quickly share credentials with the camera person's phone, then they can paste from there.
                </p>
              </div>

              <a 
                href="https://xb2025.zendesk.com/hc/en-us/articles/4662794052894-How-to-Live-Stream-in-XbotGo-App-via-RTMP"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs text-zinc-500 hover:text-cyan-400 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                XBotGo Official RTMP Streaming Guide
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepItem({ number, title, description, action, highlight }) {
  return (
    <div className={`flex gap-3 ${highlight ? 'bg-emerald-950/20 border border-emerald-800/30 rounded-lg p-3 -mx-1' : ''}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
        highlight ? 'bg-emerald-600 text-white' : 'bg-zinc-700 text-zinc-300'
      }`}>
        {number}
      </div>
      <div>
        <div className="text-sm font-medium text-white">{title}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{description}</div>
        {action}
      </div>
    </div>
  );
}
